import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import Index from "./pages/Index";
import { AuthProvider } from "./contexts/AuthContext";
import { WhatsAppModalProvider } from "./contexts/WhatsAppModalContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Lazy-loaded routes
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Login = lazy(() => import("./pages/Login"));
const Logout = lazy(() => import("./pages/Logout"));
const SobreATaxHealth = lazy(() => import("./pages/SobreATaxHealth"));
const NossosClientes = lazy(() => import("./pages/NossosClientes"));
const BeneficiosEquiparacaoHospitalar = lazy(() => import("./pages/BeneficiosEquiparacaoHospitalar"));
const OQueFazemos = lazy(() => import("./pages/OQueFazemos"));
const DescubraSuaEconomia = lazy(() => import("./pages/DescubraSuaEconomia"));
const ContentGenerate = lazy(() => import("./pages/content/ContentGenerate"));
const ContentResult = lazy(() => import("./pages/content/ContentResult"));
const ContentLibrary = lazy(() => import("./pages/content/ContentLibrary"));
const ContentGuidelines = lazy(() => import("./pages/content/ContentGuidelines"));
const ContentPost = lazy(() => import("./pages/content/ContentPost"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AdminUserEdit = lazy(() => import("./pages/admin/AdminUserEdit"));
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost"));
const BlogCategory = lazy(() => import("./pages/blog/BlogCategory"));
const BlogPreview = lazy(() => import("./pages/blog/BlogPreview"));
const Portal = lazy(() => import("./pages/Portal"));
const ContentApproval = lazy(() => import("./pages/content/ContentApproval"));
const ContentBatchApproval = lazy(() => import("./pages/content/ContentBatchApproval"));

// Initialize the query client outside of the component
const queryClient = new QueryClient();

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="loader" />
  </div>
);

// Define App as a proper function component
const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <WhatsAppModalProvider>
            <TooltipProvider>
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route 
                    path="/admin" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <Admin />
                      </ProtectedRoute>
                    } 
                  />
                  <Route path="/login" element={<Login />} />
                  <Route path="/logout" element={<Logout />} />
                  <Route 
                    path="/portal" 
                    element={
                      <ProtectedRoute requireRole={['representante', 'afiliado']}>
                        <Portal />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/content/generate" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <ContentGenerate />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/content/result/:id" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <ContentResult />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/content/library" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <ContentLibrary />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/content/guidelines" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <ContentGuidelines />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/content/post/:id" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <ContentPost />
                      </ProtectedRoute>
                    } 
                  />
                  {/* Admin User Edit */}
                  <Route 
                    path="/admin/users/:userId" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminUserEdit />
                      </ProtectedRoute>
                    } 
                  />
                  {/* Admin Blog Routes */}
                  <Route 
                    path="/admin/blog" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminBlog />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/blog/edit/:id" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminBlogEditor />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/admin/blog/new" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <AdminBlogEditor />
                      </ProtectedRoute>
                    } 
                  />
                  {/* Blog Preview (Protected) */}
                  <Route 
                    path="/admin/blog/preview/:id" 
                    element={
                      <ProtectedRoute requireAdmin={true}>
                        <BlogPreview />
                      </ProtectedRoute>
                    } 
                  />
                  {/* Public Blog Routes */}
                  <Route path="/blog" element={<BlogIndex />} />
                  <Route path="/blog/category/:slug" element={<BlogCategory />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  
                  {/* Public Content Approval */}
                  <Route path="/content/approve/:token" element={<ContentApproval />} />
                  <Route path="/content/approve-batch/:batchId" element={<ContentBatchApproval />} />
                  
                  {/* SEO Pages */}
                  <Route path="/sobre-a-taxhealth" element={<SobreATaxHealth />} />
                  <Route path="/nossos-clientes" element={<NossosClientes />} />
                  <Route path="/beneficios-da-equiparacao-hospitalar" element={<BeneficiosEquiparacaoHospitalar />} />
                  <Route path="/o-que-fazemos" element={<OQueFazemos />} />
                  <Route path="/descubra-sua-economia-com-a-taxhealth" element={<DescubraSuaEconomia />} />
                  
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <Toaster />
              <Sonner />
            </TooltipProvider>
            </WhatsAppModalProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
